import * as React from 'react';

import { translate } from '~l10n/client';

import { Action } from '~shared/components/ActionMenu/ActionMenu';
import { IconTrash } from '~shared/components/icons/standard';
import { toast } from '~shared/components/Toast';
import {
  isOverFolderLimit,
  isOverQuizLimit,
} from '~teacher/components/workspaces/utilities/checkWorkspaceItemLimit';
import { MAX_WORKSPACE_ITEMS } from '~teacher/components/workspaces/WorkspaceQuizzes/actions/constants';
import { useWorkspacePermissionsV2 } from '~teacher/hook/workspaces';
import { useIsWorkspaceReadOnly } from '~teacher/hook/workspaces/settings';
import { useDeleteWorkspaceFolders } from '~teacher/hook/workspaces/useDeleteWorkspaceFolders';
import { useDeleteWorkspaceQuizzes } from '~teacher/hook/workspaces/useDeleteWorkspaceQuizzes';
import { WorkspaceItem } from '~teacher/hook/workspaces/useWorkspaceItems';

import { WorkspaceRole } from '../../../../../api/workspaces/getWorkspaceMembers';
import { groupWorkspaceItemsByType } from '../../utilities/groupWorkspaceItemsByType';

interface UseDeleteWorkspaceItemActionParams {
  workspaceId: string;
}

export function useDeleteWorkspaceItemAction({
  workspaceId,
}: UseDeleteWorkspaceItemActionParams): Action<WorkspaceItem> {
  const deleteWorkspaceQuizMutation = useDeleteWorkspaceQuizzes();
  const deleteWorkspaceFolderMutation = useDeleteWorkspaceFolders();
  const {
    permissions: {
      deleteQuiz: canDeleteQuiz,
      deleteWorkspaceFolder: canDeleteWorkspaceFolder,
    },
    roleQuery,
  } = useWorkspacePermissionsV2({ workspaceId });
  const isReadOnly = useIsWorkspaceReadOnly(workspaceId);

  return React.useMemo(
    () => ({
      key: 'delete',
      title: translate('Delete'),
      icon: <IconTrash />,
      action: ({
        data,
        close,
      }: {
        data: WorkspaceItem[];
        close: () => void;
      }) => {
        const { folder, quiz } = groupWorkspaceItemsByType(data);

        if (isOverFolderLimit(folder) || isOverQuizLimit(quiz)) {
          toast.critical(
            translate('Please select less than {0} items').format(
              MAX_WORKSPACE_ITEMS
            )
          );
          close();
          return;
        }

        const workspaceQuizIds = quiz.map((workspaceQuiz) => workspaceQuiz.id);

        if (workspaceQuizIds.length > 0) {
          deleteWorkspaceQuizMutation.mutate(
            { workspaceQuizIds, workspaceId },
            {
              onError: () => {
                toast.critical(translate('An error has occurred'));
              },
            }
          );
        }

        const workspaceFolderIds = folder.map(
          (workspaceFolder) => workspaceFolder.id
        );

        if (workspaceFolderIds.length > 0) {
          deleteWorkspaceFolderMutation.mutate(
            {
              workspaceId,
              workspaceFolderIds,
            },
            {
              onError: () => {
                toast.critical(translate('An error has occurred'));
              },
            }
          );
        }

        close();
        return;
      },
      color: 'text-red-800',
      constraint: ({ data }) => {
        if (
          roleQuery.data === WorkspaceRole.Viewer &&
          data.every((wi) => wi.type === 'folder')
        )
          return false;

        return true;
      },
      disabled: ({ data }) => {
        if (isReadOnly) return true;
        const { quiz, folder } = groupWorkspaceItemsByType(data);
        return (
          (quiz.length >= 1 && !canDeleteQuiz) ||
          (folder.length >= 1 && !canDeleteWorkspaceFolder)
        );
      },
      divider: true,
    }),
    [
      deleteWorkspaceQuizMutation,
      workspaceId,
      deleteWorkspaceFolderMutation,
      roleQuery.data,
      canDeleteQuiz,
      canDeleteWorkspaceFolder,
      isReadOnly,
    ]
  );
}
